import React, { useCallback, useContext, useRef, useState } from 'react';

import {
    PartnerPackagingPOSortConfig,
    PartnerPackagingPOTransformConfig,
} from 'components/fbm/components/PartnerPackagingPurchaseOrders/PartnerPackagingPurchaseOrderFilterConfig';
import { PartnerPackagingPurchaseOrderFilters } from 'components/fbm/components/PartnerPackagingPurchaseOrders/PartnerPackagingPurchaseOrderFilters';
import { PartnerPackagingPurchaseOrdersTable } from 'components/fbm/components/PartnerPackagingPurchaseOrders/PartnerPackagingPurchaseOrdersTable';
import { ViewPartnerPackagingPurchaseOrderLineItemsModal } from 'components/fbm/components/PartnerPackagingPurchaseOrders/ViewPartnerPackagingPurchaseOrderLineItemsModal';
import { usePackagingPurchaseOrdersV2Search } from 'components/packaging/components/packagingPOV2/queries/PackagingPOQueries';
import { usePurchaseOrdersV2Search } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { UserContext } from 'contexts/UserContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shadcn/components/ui/Tabs';
import { FBMAccountPicker, pushToUrl, removeEmptyFilters } from 'shared';

import { PartnerPOSortConfig, PartnerPOTransformConfig } from './PartnerPurchaseOrderFilterConfig';
import { PartnerPurchaseOrderFilters } from './PartnerPurchaseOrderFilters';
import { DEFAULT_PAGE_SIZE, PartnerPurchaseOrderListShell } from './PartnerPurchaseOrderListShell';
import { PartnerPurchaseOrdersTable } from './PartnerPurchaseOrdersTable';
import { ViewPartnerPurchaseOrderLineItemsModal } from './ViewPartnerPurchaseOrderLineItemsModal';

export const TAB_PURCHASE_ORDERS = 'purchase-orders';
export const TAB_PACKAGING = 'packaging';

const PURCHASE_ORDER_TYPE = 'PURCHASE_ORDER';

const GROCERY_EMPTY_MESSAGES = {
    none: 'No purchase orders found.',
    noAccount: 'Select an FBM account to view purchase orders.',
    unmapped: 'This FBM account is not mapped to a company source. Purchase orders cannot be loaded.',
};

const PACKAGING_EMPTY_MESSAGES = {
    none: 'No packaging purchase orders found.',
    noAccount: 'Select an FBM account to view packaging purchase orders.',
    unmapped: 'This FBM account is not mapped to a company source. Packaging purchase orders cannot be loaded.',
};

function enrichGroceryApiFilters(transformed) {
    return {
        ...transformed,
        purchaseOrderTypes: [PURCHASE_ORDER_TYPE],
    };
}

function getTransformConfigForTab(tab) {
    return tab === TAB_PACKAGING ? PartnerPackagingPOTransformConfig : PartnerPOTransformConfig;
}

function clearPartnerPoUrlFilters(transformConfig) {
    pushToUrl(removeEmptyFilters({ pageSize: DEFAULT_PAGE_SIZE }), transformConfig, { replace: true });
}

/**
 * Partner purchase orders page with grocery / packaging tabs (tab state is local only).
 */
export function ViewPartnerPurchaseOrdersPage() {
    const { fbmAccount } = useContext(UserContext);
    const [tab, setTab] = useState(TAB_PURCHASE_ORDERS);
    const didClearUrlOnMountRef = useRef(false);

    // Sync clear before children initialize so the grocery shell does not read stale
    // packaging (or other) URL params after a refresh resets the tab.
    if (!didClearUrlOnMountRef.current) {
        didClearUrlOnMountRef.current = true;
        clearPartnerPoUrlFilters(PartnerPOTransformConfig);
    }

    const titlePrefix = fbmAccount?.companyName ? `${fbmAccount.companyName} - ` : '';

    const handleTabChange = useCallback((nextTab) => {
        clearPartnerPoUrlFilters(getTransformConfigForTab(nextTab));
        setTab(nextTab);
    }, []);

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <FBMAccountPicker />
                <div className="mt-2 flex items-center justify-between">
                    <h1 className="text-xl font-semibold">{titlePrefix}Purchase Orders</h1>
                </div>
            </div>

            <Tabs value={tab} onValueChange={handleTabChange} className="flex flex-1 flex-col">
                <div className="border-b border-border px-4 text-left">
                    <TabsList className="rounded-none bg-transparent p-0">
                        <TabsTrigger
                            value={TAB_PURCHASE_ORDERS}
                            className="rounded-none px-4 py-2 data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            Purchase Orders
                        </TabsTrigger>
                        <TabsTrigger
                            value={TAB_PACKAGING}
                            className="rounded-none px-4 py-2 data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            Packaging Purchase Orders
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent
                    value={TAB_PURCHASE_ORDERS}
                    className="mt-0 flex flex-1 flex-col data-[state=inactive]:hidden"
                >
                    <PartnerPurchaseOrderListShell
                        transformConfig={PartnerPOTransformConfig}
                        sortConfig={PartnerPOSortConfig}
                        useSearch={usePurchaseOrdersV2Search}
                        enrichApiFilters={enrichGroceryApiFilters}
                        Filters={PartnerPurchaseOrderFilters}
                        Table={PartnerPurchaseOrdersTable}
                        LineItemsModal={ViewPartnerPurchaseOrderLineItemsModal}
                        errorPrefix="Unable to load purchase orders."
                        emptyMessages={GROCERY_EMPTY_MESSAGES}
                    />
                </TabsContent>

                <TabsContent value={TAB_PACKAGING} className="mt-0 flex flex-1 flex-col data-[state=inactive]:hidden">
                    <PartnerPurchaseOrderListShell
                        transformConfig={PartnerPackagingPOTransformConfig}
                        sortConfig={PartnerPackagingPOSortConfig}
                        useSearch={usePackagingPurchaseOrdersV2Search}
                        Filters={PartnerPackagingPurchaseOrderFilters}
                        Table={PartnerPackagingPurchaseOrdersTable}
                        LineItemsModal={ViewPartnerPackagingPurchaseOrderLineItemsModal}
                        errorPrefix="Unable to load packaging purchase orders."
                        emptyMessages={PACKAGING_EMPTY_MESSAGES}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
